import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight, Cloud, Wind, Eye, Thermometer, Gauge, Clock, CheckCircle, XCircle, Plane, Star, Users, Award, Sparkles, Bot, User, Send, X } from 'lucide-react';
import { Analytics } from "@vercel/analytics/react"

const App = () => {
  const [currentScreen, setCurrentScreen] = useState('landing');
  const [currentStep, setCurrentStep] = useState(0);
  const [showStepFeedback, setShowStepFeedback] = useState(false);
  const [componentQuizAnswers, setComponentQuizAnswers] = useState({});
  const [email, setEmail] = useState('');
  const [hasSignedUp, setHasSignedUp] = useState(false);
  
  // AI Helper state
  const [selectedText, setSelectedText] = useState('');
  const [showAIHelp, setShowAIHelp] = useState(false);
  const [helpButtonPosition, setHelpButtonPosition] = useState({ x: 0, y: 0 });
  const [showAIChat, setShowAIChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [isAITyping, setIsAITyping] = useState(false);

  // Handle text selection for AI help
  useEffect(() => {
    const handleSelection = () => {
      const selection = window.getSelection();
      const text = selection.toString().trim();
      
      if (text.length > 0 && text.length < 100 && currentScreen === 'lesson') {
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        
        setSelectedText(text);
        setHelpButtonPosition({
          x: rect.left + rect.width / 2,
          y: rect.top - 10
        });
        setShowAIHelp(true);
      } else {
        setShowAIHelp(false);
      }
    };

    document.addEventListener('mouseup', handleSelection);
    document.addEventListener('touchend', handleSelection);
    
    return () => {
      document.removeEventListener('mouseup', handleSelection);
      document.removeEventListener('touchend', handleSelection);
    };
  }, [currentScreen]);

  // Mock AI responses
  const getAIResponse = (text) => {
    const responses = {
      'KJFK': {
        message: "KJFK is the ICAO code for John F. Kennedy International Airport. The 'K' means it's in the US, and 'JFK' identifies the specific airport.",
        followUp: "Every US airport starts with 'K'. Can you guess what KLAX stands for?"
      },
      '28015G25KT': {
        message: "Wind from 280° at 15 knots, gusting to 25 knots. That's a westerly wind with some bumps!",
        followUp: "Pro tip: Always add half the gust factor to your approach speed for safety."
      },
      '10SM': {
        message: "10 statute miles visibility - that's crystal clear! You can see for miles in every direction.",
        followUp: "Fun fact: VFR only needs 3 miles, so 10SM is excellent flying weather!"
      },
      'Z': {
        message: "'Z' stands for Zulu time (UTC). Aviation uses this worldwide to avoid time zone confusion.",
        followUp: "Pilots always use Zulu time for flight planning!"
      }
    };

    for (const [key, response] of Object.entries(responses)) {
      if (text.includes(key)) return response;
    }

    return {
      message: `"${text}" is part of the weather code. Each piece tells pilots something important about flying conditions.`,
      followUp: "Keep learning - you're doing great!"
    };
  };

  const handleAskAI = () => {
    setShowAIHelp(false);
    setShowAIChat(true);
    
    const userMessage = {
      type: 'user',
      content: `I don't understand: "${selectedText}"`
    };
    
    setChatMessages([...chatMessages, userMessage]);
    setIsAITyping(true);
    
    setTimeout(() => {
      const response = getAIResponse(selectedText);
      setChatMessages(prev => [...prev, {
        type: 'ai',
        content: response.message,
        followUp: response.followUp
      }]);
      setIsAITyping(false);
    }, 1000);
  };

  // Sample METAR for the lesson
  const sampleMETAR = "KJFK 121651Z 28015G25KT 10SM FEW050 SCT100 BKN250 20/M03 A2992";

  // Lesson steps
  const lessonSteps = [
    {
      id: 'intro',
      type: 'narrative',
      content: {
        title: "Your First Weather Briefing",
        text: "You're checking weather before your flight. Your instructor asks: 'What does the METAR say?'",
        subtext: "You look at the screen and see...",
        metar: sampleMETAR
      }
    },
    {
      id: 'station',
      component: 'KJFK',
      title: 'Airport Code',
      format: 'CCCC (4 letters)',
      explanation: 'KJFK = John F. Kennedy Airport in New York',
      meaning: 'Tells you where this weather report is from',
      quiz: {
        question: 'Which airport is reporting?',
        options: ['Los Angeles (LAX)', 'New York JFK', 'Chicago ORD'],
        correct: 'New York JFK'
      }
    },
    {
      id: 'time',
      component: '121651Z',
      title: 'Time',
      format: 'DDHHMM + Z',
      explanation: '12th day, 16:51 UTC (Zulu time)',
      meaning: 'When this report was issued - critical for currency',
      quiz: {
        question: 'What time was this report issued?',
        options: ['12:16 UTC', '16:51 UTC', '16:12 UTC'],
        correct: '16:51 UTC'
      }
    },
    {
      id: 'wind',
      component: '28015G25KT',
      title: 'Wind',
      format: 'DDDFFGGGKT',
      explanation: 'From 280° at 15 knots, gusting to 25',
      meaning: 'Expect crosswinds and turbulence on approach!',
      quiz: {
        question: 'How strong are the gusts?',
        options: ['15 knots', '25 knots', '28 knots'],
        correct: '25 knots'
      }
    },
    {
      id: 'complete',
      type: 'summary'
    }
  ];

  const currentStepData = lessonSteps[currentStep];

  const handleQuizAnswer = (answer) => {
    setComponentQuizAnswers({ ...componentQuizAnswers, [currentStepData.id]: answer });
    setShowStepFeedback(true);
    
    setTimeout(() => {
      setShowStepFeedback(false);
      if (currentStep < lessonSteps.length - 1) {
        setCurrentStep(currentStep + 1);
      }
    }, 1500);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setHasSignedUp(true);
      // Here you would normally send to your backend
      console.log('Email captured:', email);
    }
  };

  // Landing Page Component
  const LandingPage = () => (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      {/* Hero Section */}
      <div className="max-w-6xl mx-auto px-6 pt-12 pb-20">
        <nav className="flex justify-between items-center mb-16">
          <div className="flex items-center gap-2">
            <Plane className="text-blue-600" size={28} />
            <span className="font-bold text-xl">Ailaron Academy</span>
          </div>
          <button 
            onClick={() => setCurrentScreen('lesson')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Try Free Lesson
          </button>
        </nav>

        <div className="text-center space-y-6 animate-fade-in">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 leading-tight">
            Don't Just Memorize METARs —<br />
            <span className="text-blue-600">Actually Understand Them.</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Learn to read weather reports like a real pilot with short, interactive lessons that make it click.
          </p>

          {/* METAR Challenge */}
          <div className="bg-gray-900 rounded-lg p-6 max-w-2xl mx-auto shadow-2xl animate-fade-in mt-8 hover:shadow-3xl transition-all duration-300 cursor-pointer group" 
               style={{ animationDelay: '0.3s' }}
               onClick={() => setCurrentScreen('lesson')}>
            <p className="text-gray-400 text-sm mb-2">Can you decode this weather report?</p>
            <p className="text-green-400 font-mono text-lg mb-4 group-hover:text-green-300 transition-colors">
              METAR KJFK 121651Z 28015G25KT 10SM FEW050 20/M03 A2992
            </p>
            <p className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">
              Don't worry if it looks like gibberish - you'll understand every piece in 5 minutes ↓
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-6">
            <button 
              onClick={() => setCurrentScreen('lesson')}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg hover:bg-blue-700 transition-all transform hover:scale-105 font-semibold text-lg shadow-lg flex items-center gap-2"
            >
              Decode This METAR <ChevronRight />
            </button>
            <p className="text-gray-500">No signup required • 5 min lesson</p>
          </div>
        </div>

        {/* Value Props */}
        <div className="grid md:grid-cols-3 gap-8 mt-20">
          <div className="text-center space-y-3 animate-fade-in" style={{ animationDelay: '0.2s' }}>
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
              <Sparkles className="text-blue-600" size={28} />
            </div>
            <h3 className="font-semibold text-lg">Microlearning That Clicks</h3>
            <p className="text-gray-600">Learn complex topics in 2–5 minute lessons, one step at a time.</p>
          </div>
          
          <div className="text-center space-y-3 animate-fade-in" style={{ animationDelay: '0.3s' }}>
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
              <Bot className="text-blue-600" size={28} />
            </div>
            <h3 className="font-semibold text-lg">Smart Feedback in Real Time</h3>
            <p className="text-gray-600">Get instant explanations, not just answers.</p>
          </div>
          
          <div className="text-center space-y-3 animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto">
              <Award className="text-blue-600" size={28} />
            </div>
            <h3 className="font-semibold text-lg">Master It — Don't Just Memorize It</h3>
            <p className="text-gray-600">Practice with FAA-style questions, backed by spaced repetition and confidence-based learning.</p>
          </div>
        </div>

        {/* What is Ailaron Academy */}
        <div className="mt-20 bg-blue-50 rounded-2xl p-8 max-w-4xl mx-auto animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="text-center space-y-4">
            <h2 className="text-3xl font-bold text-gray-900">What is Ailaron Academy?</h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto">
              Ailaron Academy is a reimagined ground school for student pilots. 
              We teach the hardest aviation topics — weather, airspace, comms, and more — through bite-sized lessons, unlimited practice, and real-world context.
          </p>
            <p className="text-lg text-blue-600 font-medium">
              No textbooks. No memorization. Just real understanding that sticks.
            </p>
          </div>
        </div>

        {/* Testimonial */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md mx-auto mb-8">
            <div className="flex justify-center mb-3">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="text-yellow-400 fill-current" size={20} />
              ))}
            </div>
            <p className="text-gray-700 italic mb-3">
              "Finally something that made METARs click. I went from confused to confident in one lesson."
            </p>
            <p className="text-gray-600 text-sm">— Sarah, student pilot</p>
          </div>
        </div>

        {/* Social Proof */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 text-gray-600">
            <Users size={20} />
            <span>Join 1,247 student pilots learning the smart way</span>
          </div>
        </div>
      </div>
    </div>
  );

  // Lesson Component
  const Lesson = () => {
    if (currentStepData.type === 'narrative') {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
          <div className="max-w-2xl w-full text-center space-y-6 animate-fade-in">
            <h2 className="text-3xl font-bold">{currentStepData.content.title}</h2>
            <p className="text-lg text-gray-700">{currentStepData.content.text}</p>
            <p className="text-gray-600">{currentStepData.content.subtext}</p>
            <div className="bg-gray-900 text-green-400 p-6 rounded-lg font-mono text-lg">
              {currentStepData.content.metar}
            </div>
            <p className="text-gray-500 italic">Looks like code, right? Let's decode it together.</p>
            <button 
              onClick={() => setCurrentStep(currentStep + 1)}
              className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
            >
              Start Decoding →
            </button>
          </div>
        </div>
      );
    }

    if (currentStepData.type === 'summary') {
      return (
        <div className="min-h-screen flex items-center justify-center p-6 bg-gray-50">
          <div className="max-w-md w-full text-center space-y-6 animate-fade-in">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold">You Did It!</h2>
            <p className="text-lg text-gray-700">
              You just decoded your first METAR components. With practice, this becomes second nature.
            </p>
            
            {!hasSignedUp ? (
              <div className="bg-white rounded-lg shadow-lg p-6 mt-8">
                <h3 className="font-semibold text-lg mb-3">Like this? Get early access to the full platform.</h3>
                <p className="text-gray-600 mb-4">Join the waitlist for Ailaron Academy:</p>
                <ul className="text-left text-sm text-gray-600 space-y-2 mb-6">
                  <li>✓ Complete weather, airspace, and comms lessons</li>
                  <li>✓ Real-world scenarios and case studies</li>
                  <li>✓ Spaced repetition and confidence-based learning</li>
                  <li>✓ AI instructor for unlimited help</li>
                </ul>
                <form onSubmit={handleEmailSubmit} className="space-y-3">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="pilot@example.com"
                    className="w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <button 
                    type="submit"
                    className="w-full bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                  >
                    Join the Waitlist
                  </button>
                </form>
              </div>
            ) : (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6">
                <CheckCircle className="text-green-600 mx-auto mb-3" size={48} />
                <h3 className="font-semibold text-lg text-green-800">You're on the list!</h3>
                <p className="text-green-700">We'll email you as soon as the full course is ready.</p>
              </div>
            )}

            <button 
              onClick={() => {
                setCurrentScreen('landing');
                setCurrentStep(0);
                setComponentQuizAnswers({});
              }}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Home
            </button>
          </div>
        </div>
      );
    }

    // Regular lesson step
    return (
      <div className="min-h-screen p-6 bg-gray-50">
        {/* Simple Header */}
        <div className="max-w-4xl mx-auto mb-8">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold">Decoding: {currentStepData.title}</h2>
            <button
              onClick={() => setShowAIChat(!showAIChat)}
              className="text-purple-600 hover:text-purple-700 flex items-center gap-1 text-sm font-medium"
            >
              <Bot size={18} />
              AI Help
            </button>
          </div>
          <p className="text-gray-600 text-sm mt-1">Step {currentStep} of {lessonSteps.length - 1}</p>
        </div>

        {/* METAR with highlight */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-900 text-green-400 p-6 rounded-lg font-mono text-lg mb-8">
            {sampleMETAR.split(' ').map((part, index) => (
              <span key={index} className={part === currentStepData.component ? 'bg-yellow-500 text-black px-1 rounded' : ''}>
                {part}{' '}
              </span>
            ))}
          </div>

          {/* Component explanation */}
          <div className="bg-white rounded-lg shadow-lg p-6 animate-fade-in">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                {currentStepData.id === 'station' && <Plane className="text-blue-600" size={24} />}
                {currentStepData.id === 'time' && <Clock className="text-blue-600" size={24} />}
                {currentStepData.id === 'wind' && <Wind className="text-blue-600" size={24} />}
              </div>
              <div>
                <h3 className="text-xl font-bold">{currentStepData.component}</h3>
                <p className="text-gray-500 text-sm">Format: {currentStepData.format}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-lg font-medium text-gray-800">{currentStepData.explanation}</p>
              </div>
              
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4">
                <p className="text-blue-900 font-medium">What this means for your flight:</p>
                <p className="text-blue-800">{currentStepData.meaning}</p>
              </div>

              {/* Quiz */}
              <div className="bg-gray-50 rounded-lg p-6 mt-6">
                <h4 className="font-semibold mb-3">{currentStepData.quiz.question}</h4>
                <div className="space-y-2">
                  {currentStepData.quiz.options.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleQuizAnswer(option)}
                      disabled={showStepFeedback}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        showStepFeedback && componentQuizAnswers[currentStepData.id] === option
                          ? option === currentStepData.quiz.correct
                            ? 'bg-green-100 border-green-500'
                            : 'bg-red-100 border-red-500'
                          : 'bg-white border-gray-300 hover:border-blue-400'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {showStepFeedback && (
                  <div className={`mt-4 flex items-center gap-2 ${
                    componentQuizAnswers[currentStepData.id] === currentStepData.quiz.correct ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {componentQuizAnswers[currentStepData.id] === currentStepData.quiz.correct ? (
                      <>
                        <CheckCircle /> Correct! Moving on...
                      </>
                    ) : (
                      <>
                        <XCircle /> Not quite. It's {currentStepData.quiz.correct}.
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // AI Help Button
  const AIHelpButton = () => {
    if (!showAIHelp) return null;
    
    return (
      <div 
        className="fixed z-50 animate-fade-in"
        style={{ 
          left: `${helpButtonPosition.x}px`, 
          top: `${helpButtonPosition.y}px`,
          transform: 'translate(-50%, -100%)'
        }}
      >
        <button
          onClick={handleAskAI}
          className="bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium shadow-lg hover:bg-purple-700 transition-all flex items-center gap-1"
        >
          <Sparkles size={14} />
          Explain this
        </button>
      </div>
    );
  };

  // AI Chat Interface
  const AIChatInterface = () => {
    const chatRef = useRef(null);
    
    if (!showAIChat) return null;
    
    return (
      <div className="fixed right-0 top-0 h-full w-96 bg-white shadow-2xl z-40 flex flex-col animate-slide-in-right">
        <div className="bg-purple-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Bot size={24} />
            <div>
              <h3 className="font-semibold">AI Instructor</h3>
              <p className="text-xs opacity-90">Highlight text to ask about it!</p>
            </div>
          </div>
          <button 
            onClick={() => setShowAIChat(false)}
            className="hover:bg-purple-700 p-1 rounded"
          >
            <X size={20} />
          </button>
        </div>
        
        <div ref={chatRef} className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 chat-scroll">
          {chatMessages.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <p>Highlight any text in the lesson to get instant explanations!</p>
            </div>
          )}
          
          {chatMessages.map((msg, index) => (
            <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] rounded-lg p-3 ${
                msg.type === 'user' ? 'bg-blue-600 text-white' : 'bg-white shadow'
              }`}>
                <p>{msg.content}</p>
                {msg.followUp && (
                  <p className="mt-2 text-sm opacity-80 italic">
                    💡 {msg.followUp}
                  </p>
                )}
              </div>
            </div>
          ))}
          
          {isAITyping && (
            <div className="flex justify-start">
              <div className="bg-white shadow rounded-lg p-3">
                <div className="flex gap-1">
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      {currentScreen === 'landing' && <LandingPage />}
      {currentScreen === 'lesson' && <Lesson />}
      <AIHelpButton />
      <AIChatInterface />
      <Analytics/>
    </>
  );
};

export default App; 